// Phase 10B "AutoMind 2050" — Recent SMS evaluations table.
// Reads /api/sms-followups; displays redacted recipients only and never
// renders any preview/body field.
import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Inbox } from 'lucide-react';

import { DataCard } from '../../components/ui/data-card';
import { StatusPill } from '../../components/ui/status-pill';
import { EmptyState } from '../../components/ui/empty-state';
import { smsFollowupsApi } from '../../api';
import {
  STATUS_TONES,
  STATUS_LABELS,
  REASON_LABELS,
  BLOCK_REASON_LABELS,
  STATUS_FILTER_OPTIONS,
  REASON_FILTER_OPTIONS,
} from './constants';

const PAGE_SIZE = 20;

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function shortId(id) {
  if (!id) return '—';
  return String(id).slice(0, 8);
}

function SkeletonRow() {
  return (
    <tr className="border-b border-ink-200/60 dark:border-ink-800/60">
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} className="py-3 pr-3">
          <div className="h-3 w-24 skeleton" />
        </td>
      ))}
    </tr>
  );
}

export default function EvaluationsTable({ dealerId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', reason: '' });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (filters.status) params.status = filters.status;
      if (filters.reason) params.reason = filters.reason;
      if (dealerId) params.dealer_id = dealerId;
      const res = await smsFollowupsApi.list(params);
      setItems(res?.items || []);
      setTotal(res?.total || 0);
    } catch (err) {
      setError(err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [dealerId, filters.status, filters.reason, offset]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Reset paging when a filter changes.
  useEffect(() => {
    setOffset(0);
  }, [filters.status, filters.reason, dealerId]);

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + items.length, total);
  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  const renderReason = (item) => {
    if (item.status === 'blocked' || item.status === 'skipped') {
      const k = item.blocked_by;
      if (!k) return '—';
      return BLOCK_REASON_LABELS[k] || k;
    }
    return '—';
  };

  const renderSentAt = (item) => {
    if (item.sent_at) return formatDateTime(item.sent_at);
    if (item.earliest_send_at) {
      const d = new Date(item.earliest_send_at);
      const inFuture = !isNaN(d.getTime()) && d.getTime() > Date.now();
      if (inFuture) {
        return (
          <span className="inline-flex items-center gap-1.5 text-ink-500">
            <Clock className="w-3 h-3" />
            {formatDateTime(item.earliest_send_at)}
          </span>
        );
      }
    }
    return '—';
  };

  const headers = [
    'Time',
    'Recipient',
    'Trigger',
    'Template',
    'Status',
    'Reason',
    'Sent',
  ];

  return (
    <DataCard title="Recent SMS decisions" description="Last 14 days">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          className="h-9 text-sm rounded-md border border-ink-200 dark:border-ink-800 bg-white/70 dark:bg-ink-900/40 px-2 focus-2050"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          data-testid="sms-filter-status"
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value || 'all-status'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className="h-9 text-sm rounded-md border border-ink-200 dark:border-ink-800 bg-white/70 dark:bg-ink-900/40 px-2 focus-2050"
          value={filters.reason}
          onChange={(e) => setFilters((f) => ({ ...f, reason: e.target.value }))}
          data-testid="sms-filter-reason"
        >
          {REASON_FILTER_OPTIONS.map((o) => (
            <option key={o.value || 'all-reason'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-xs text-ink-500 ml-auto nums">
          {total === 0 ? '—' : `${showingFrom}–${showingTo} of ${total}`}
        </span>
      </div>

      {error ? (
        <EmptyState
          icon={Inbox}
          title="Unable to load right now."
          description="Try again in a moment."
        />
      ) : !loading && items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No SMS decisions yet"
          description="Once automation runs against new calls, the log shows up here."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sms-evaluations-table">
            <thead>
              <tr className="text-left border-b border-ink-200/60 dark:border-ink-800/60">
                {headers.map((h) => (
                  <th
                    key={h}
                    className="py-2 pr-3 text-xs uppercase tracking-[0.16em] text-ink-500 font-medium"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                : items.map((item) => (
                    <tr
                      key={item.id}
                      className="row-2050 align-top"
                      data-testid={`sms-row-${item.id}`}
                    >
                      <td className="py-3 pr-3 whitespace-nowrap text-ink-500 nums">
                        {formatDateTime(item.created_at)}
                      </td>
                      <td className="py-3 pr-3 font-mono text-xs text-ink-900 dark:text-ink-50">
                        {item.recipient_redacted || '—'}
                      </td>
                      <td className="py-3 pr-3 text-ink-900 dark:text-ink-50">
                        {REASON_LABELS[item.reason] || item.reason || '—'}
                      </td>
                      <td className="py-3 pr-3 font-mono text-xs text-ink-500">
                        {shortId(item.template_id)}
                      </td>
                      <td className="py-3 pr-3">
                        <StatusPill
                          tone={STATUS_TONES[item.status] || 'neutral'}
                          label={STATUS_LABELS[item.status] || item.status}
                        />
                      </td>
                      <td className="py-3 pr-3 text-ink-500">
                        {renderReason(item)}
                      </td>
                      <td className="py-3 pr-3 whitespace-nowrap text-ink-500">
                        {renderSentAt(item)}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!error && (loading || items.length > 0) && (
        <div className="flex items-center justify-end gap-2 pt-4">
          <button
            type="button"
            className="btn-2050 btn-secondary"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={!canPrev || loading}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn-2050 btn-secondary"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!canNext || loading}
          >
            Next
          </button>
        </div>
      )}
    </DataCard>
  );
}
